import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from '@/components/ui/form'
import { RotateCcw } from 'lucide-react'
import { PropsWithChildren, ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Card from '../Card'
import { cn } from '@/lib/utils'

interface Props {
  label: string | ReactNode
  description?: ReactNode
  layout?: 'box' | 'inline' | 'stacked' | 'inline-shrinkable'
  resetValue?: string
  className?: string
}

const FieldWrapper = ({
  children,
  className,
  layout = 'inline',
  label,
  description,
  resetValue,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation()
  const form = useFormContext()
  const field = useFormField()
  const currentValue = form.watch(field.name)
  const showReset =
    layout === 'inline' &&
    resetValue !== undefined &&
    currentValue !== resetValue

  const Component = layout === 'box' ? Card : 'div'

  return (
    <Component
      className={cn(
        layout === 'stacked' || layout === 'inline-shrinkable' ? '' : 'mb-6',
        layout === 'box' ? 'relative' : '',
      )}
    >
      <FormItem
        className={`${layout === 'inline-shrinkable' ? '' : 'grow'} ${
          className ?? ''
        }`}
      >
        {label ? (
          <FormLabel
            className={
              layout === 'box'
                ? 'mr-24'
                : 'inline-flex w-full items-center justify-between'
            }
          >
            {label}
            {showReset ? (
              <a
                onClick={() =>
                  form.setValue(field.name, resetValue, { shouldDirty: true })
                }
                className='cursor-pointer font-normal flex gap-1.5 items-center hover:text-white'
              >
                <RotateCcw size={14} /> {t('shared.reset')}
              </a>
            ) : null}
          </FormLabel>
        ) : null}
        <FormControl
          className={layout === 'box' ? 'absolute right-4 top-2.5' : ''}
        >
          {children}
        </FormControl>
        {layout !== 'stacked' || description ? (
          <FormDescription className={layout === 'box' ? 'mr-32' : ''}>
            {description}
          </FormDescription>
        ) : null}
        <FormMessage />
      </FormItem>
    </Component>
  )
}

export default FieldWrapper
